/** Board display controls choose presentation values from the same metadata pool used by server projection. */
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject, input, output, computed } from '@angular/core';
import { Command } from '@workbuddy/ui/model';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { SettingsPanelComponent, SettingsPanelBuilder, SettingsPanelForm, displayTypeCommand } from '@workbuddy/ui/v2/blocks/settings.panel';
import { resolveDisplayTypes } from '../view.settings/adapters/resolve.display.types';
import { resolveGroupFields } from '../view.settings/adapters/resolve.group.fields';
import { EntityView, EntityViewDisplay, EntityViewSortDirection, isGanttColumnField, resolveParentLevels } from '@jms/model';
import { appendDisplaySettingsContributions, contributionFormValuesToViewFragment, getDisplaySettingsPathValue, resolveActiveDisplaySettingsContributions, type EntityBoardDisplaySettingsContribution, type EntityBoardDisplaySettingsExtension } from '../../context/entity.board.display.settings';
import { EntityBoardListConfig, constrainListDisplayPropertySelection, listDisplayPropertiesAllow, listDisplayPropertiesEnabled } from '../../context/entity.board.list';
import { EntityBoardReorderConfig } from '../../context/entity.board.reorder';
import { resolveListDisplayPropertyFields, resolveUiFields } from '../../state/features/with.metadata';
import { ViewSettingsConfig } from '../view.settings/view.settings.model';
import { viewSettingsBridgeStyles, viewSettingsStyleId } from '../view.settings/view.settings.theme';
import { boardPageSizeOptions, clampUnvirtualizedBoardPageSize } from '../../board.page.size';

import { ganttFormToConfig } from '../view.settings/adapters/gantt.display.adapter';

function toOptionsWithNone(fields: any[]) { return [{ id: 'none', label: 'None' }, ...(fields ?? []).map(f => ({ id: f.path ?? f.name, label: f.displayName ?? f.name }))]; }

/**
 * Group keys a second key may be stacked on: the row grain's ancestors, by identifier path.
 *
 * `null` means "no embedded grain here" — a Primary board is not unwound, so nothing pages by group
 * and Then by costs the reader nothing. Only an embedded board has to choose between the two grains.
 */
function resolveParentGroupPaths(config: ViewSettingsConfig): Set<string> | null {

  const parents = resolveParentLevels(config.metadata, config.view?.recordScope ?? null);
  if (!parents.length) return null;

  return new Set(parents.map(parent => parent.identifierPath));
}

/**
 * Table/list Group options — the same pool the Kanban Row axis takes (`resolveGroupFields`):
 * parents of the row grain, then the row's own groupable fields.
 *
 * Scoped fields are recomputed here rather than read from `config.groupableFields`, which is the
 * board store's UNSCOPED projection: on a Stages board that mixes Job and Stage fields with no way
 * to tell them apart. `resolveUiFields(metadata, scope)` is what the view editor already builds against.
 *
 * The old None | Job hardcode dated from server paging keeping only PARENT groups whole; whole-group
 * paging now covers row-level keys too (`mongo.row.group.paging.ts`), so a stage field is a real group.
 */
function resolveGroupableOptions(config: ViewSettingsConfig): { id: string; label: string }[] {

  const metadata = config.metadata;
  if (!metadata) return toOptionsWithNone(config.groupableFields ?? []);

  const scope = config.view?.recordScope ?? null;
  return toOptionsWithNone(resolveGroupFields(metadata, scope, resolveUiFields(metadata, scope)));
}

function normalizeGroupBy(value: string | null | undefined): string | null {

  if (value == null || value === '' || value === 'none') return null;
  return value;
}

/** True when the view already stores a value for any active contribution path. */
function viewHasContributionPathValues(
  contributions: readonly EntityBoardDisplaySettingsContribution[],
  view: EntityView | null | undefined,
): boolean {

  for (const contrib of contributions) {
    for (const control of contrib.controls ?? []) {
      if (getDisplaySettingsPathValue(view, control.path) !== undefined) return true;
    }
  }
  return false;
}

/** Shallow merge of plain objects; returns undefined when both sides are empty. */
function mergePlainObjects(
  a: Record<string, unknown> | null | undefined,
  b: Record<string, unknown> | null | undefined,
): Record<string, unknown> | undefined {

  if (!a && !b) return undefined;
  if (!a) return b ? { ...b } : undefined;
  if (!b) return { ...a };
  return { ...a, ...b };
}

/**
 * Active sort for Ordering/Sorting. Prefer view.sort; list boards also stamp list.sortBy.
 * (Task job-tasks-list uses sortOrder which is metadata-hidden and missing from uiFields.)
 */
function resolveCurrentSort(view: EntityView | null): { id: string; direction: EntityViewSortDirection } {

  const list = view?.list as { sortBy?: string | null; sortOrder?: EntityViewSortDirection } | undefined;
  const id = view?.sort?._id ?? list?.sortBy ?? null;
  if (id == null || id === '' || id === 'none') {
    return { id: 'none', direction: view?.sort?.direction ?? list?.sortOrder ?? EntityViewSortDirection.Asc };
  }
  return {
    id: String(id),
    direction: view?.sort?.direction ?? list?.sortOrder ?? EntityViewSortDirection.Asc,
  };
}

/**
 * Sort dropdown options + always include the active sort field so the trigger is never blank
 * when the view sorts by a hidden/non-display field (e.g. Task `sortOrder`).
 */
function resolveSortOptions(
  sortableFields: any[],
  currentSortId: string,
  uiFields: any[],
  metadata: { fields?: any[] } | null | undefined,
  manualSortField: string | null,
): { id: string; label: string }[] {

  const options = (sortableFields ?? []).map(f => ({ id: f.path ?? f.name, label: f.displayName ?? f.name }));
  if (!manualSortField) options.unshift({ id: 'none', label: 'None' });
  if (!currentSortId || currentSortId === 'none') return options;
  if (options.some(o => String(o.id) === currentSortId)) return options;

  const fromUi = (uiFields ?? []).find(f => (f.path ?? f.name) === currentSortId);
  const fromMeta = (metadata?.fields ?? []).find((f: any) => (f.path ?? f.name) === currentSortId);
  const label = fromUi?.displayName ?? fromUi?.name ?? fromMeta?.displayName ?? fromMeta?.name ?? currentSortId;
  const insertAt = options[0]?.id === 'none' ? 1 : 0;
  return [...options.slice(0, insertAt), { id: currentSortId, label: String(label) }, ...options.slice(insertAt)];
}

/**
 * Chip order = Linear "order of appearance" on the row:
 * 1. board.list.displayProperties.allow (product order)
 * 2. currently selected fields (only when there is no allowlist)
 * 3. remaining candidates
 */
function orderListDisplayPropertyFields(
  byId: Map<string, { path?: string; name?: string; displayName?: string }>,
  allow: string[] | undefined,
  selected: string[],
): { path?: string; name?: string; displayName?: string }[] {

  const result: { path?: string; name?: string; displayName?: string }[] = [];
  const seen = new Set<string>();
  const push = (id: string) => {
    if (!id || seen.has(id)) return;
    const field = byId.get(id);
    if (!field) return;
    seen.add(id);
    result.push(field);
  };
  for (const id of allow ?? []) push(String(id));
  for (const id of selected) push(String(id));
  for (const id of byId.keys()) push(id);
  return result;
}

/** Sort by appearance order; keep unknown ids at the end (never drop selections). */
function sortIdsByAppearance(ids: string[], appearanceOrder: string[]): string[] {

  const rank = new Map(appearanceOrder.map((id, i) => [id, i]));
  return [...ids].sort((a, b) => {
    const ra = rank.has(String(a)) ? rank.get(String(a))! : Number.MAX_SAFE_INTEGER;
    const rb = rank.has(String(b)) ? rank.get(String(b))! : Number.MAX_SAFE_INTEGER;
    return ra - rb;
  });
}

/** Display-settings update containing the partial view, including table grouping, and effective page size. */
export type DisplaySettingsValue = {
  view: Partial<EntityView>;
  pageSize: number;
  /** True when the user changed the layout, so the host reloads rather than only re-rendering. */
  displayChanged?: boolean;
};

@Component({
  selector: 'display-settings',
  template: `<wb-settings-panel [panel]="panel()" [dismissOnClickOutside]="true" (closed)="closed.emit()" />`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SettingsPanelComponent],
})
export class DisplaySettingsComponent {

  private readonly document = inject(DOCUMENT);

  public $config = input.required<ViewSettingsConfig>({ alias: 'config' });
  public $lockDisplay = input<boolean>(false, { alias: 'lockDisplay' });
  /** Board types offered by the switcher (from the entity manifest). Null = the generic default set. */
  public $displayTypes = input<EntityViewDisplay[] | null>(null, { alias: 'displayTypes' });
  /** Mount opt-in (`header.showDisplaySwitcher`). Without it the panel offers no layout choice. */
  public $showDisplaySwitcher = input<boolean>(false, { alias: 'showDisplaySwitcher' });
  /** Whether the signed-in user may rewrite the view itself, gating "Set as default for everyone". */
  public $canEditView = input<boolean>(false, { alias: 'canEditView' });
  /**
   * The layout the VIEW declares, before this user's owned choice is layered on.
   *
   * `config.view` is the effective view, so its display already reflects the personal override —
   * comparing against it could never tell "my layout differs from the shared default" apart.
   */
  public $viewDisplay = input<EntityViewDisplay | null>(null, { alias: 'viewDisplay' });
  /** Whether this entity supports the document preview pane (financial document + manifest detail
   *  pane). Gates the per-view "Show Preview" toggle — bound from the board store's $previewSupported. */
  public $previewSupported = input<boolean>(false, { alias: 'previewSupported' });
  /** True when the board request is paged, including focus layout's footer paginator. */
  public $paged = input<boolean>(true, { alias: 'paged' });
  /**
   * List-only board policy (`board.list`). Gates Linear-style list options such as display
   * properties. Null/undefined = no list options.
   */
  public $listConfig = input<EntityBoardListConfig | null>(null, { alias: 'listConfig' });
  /** Compact-only List settings are omitted entirely from regular board layouts. */
  public $compact = input<boolean>(false, { alias: 'compact' });
  /** Manual rank field used by the shared board reorder capability. */
  public $reorderConfig = input<EntityBoardReorderConfig | null>(null, { alias: 'reorderConfig' });
  /**
   * Generic display-settings contributions from board context / manifest.
   * Shell paints control kinds only — no entity- or display-product branches.
   */
  public $displaySettings = input<EntityBoardDisplaySettingsExtension | null>(null, { alias: 'displaySettings' });
  /** Parent-scoped mount (for contribution `when` predicates). */
  public $isEmbedded = input<boolean>(false, { alias: 'isEmbedded' });

  /** Emits the effective partial view and page size after a display-setting change. */
  public valueChange = output<DisplaySettingsValue>();
  public closed = output<void>();
  public resetRequested = output<void>();
  /** "Set as default for everyone" — the host writes `display` to the view and drops the personal one. */
  public defaultDisplayRequested = output<EntityViewDisplay>();

  protected panel = computed(() => this.buildPanel(this.$config(), this.$lockDisplay(), this.$displayTypes(), this.$previewSupported(), this.$paged(), this.$listConfig(), this.$compact(), this.$reorderConfig(), this.$displaySettings(), this.$isEmbedded(), this.$showDisplaySwitcher(), this.$canEditView(), this.$viewDisplay()));

  public constructor() {

    DesignSystem.Mount(this.document, viewSettingsStyleId, viewSettingsBridgeStyles);
  }

  private buildPanel(config: ViewSettingsConfig, lockDisplay: boolean, displayTypes: EntityViewDisplay[] | null, previewSupported: boolean, paged: boolean, listConfig: EntityBoardListConfig | null, compact: boolean, reorderConfig: EntityBoardReorderConfig | null, displaySettings: EntityBoardDisplaySettingsExtension | null, isEmbedded: boolean, showDisplaySwitcher: boolean, canEditView: boolean, viewDisplay: EntityViewDisplay | null): SettingsPanelForm {

    const { view, sortableFields } = config;
    const groupableOptions = resolveGroupableOptions(config);
    const table = view?.table as { groupBy?: string | null; subGroupBy?: string | null } | undefined;
    const list = view?.list as { groupBy?: string | null; fields?: string[]; titleLines?: 1 | 2 | 3; showEmptyGroups?: boolean; showNestedItems?: boolean; indentNestedItems?: boolean } | undefined;
    // Match board.list / board.table: owned table.groupBy wins (null = None), else system list.groupBy.
    const currentGroupBy = normalizeGroupBy(
      table && Object.prototype.hasOwnProperty.call(table, 'groupBy') ? table.groupBy : list?.groupBy,
    ) ?? 'none';
    const currentSort = resolveCurrentSort(view);
    const manualSortField = reorderConfig?.field ?? (reorderConfig ? 'sortOrder' : null);
    const selectedSortId = manualSortField && currentSort.id === 'none' ? manualSortField : currentSort.id;
    const sortableOptions = resolveSortOptions(sortableFields, selectedSortId, config.uiFields ?? [], config.metadata, manualSortField);
    const sortCommands = [new Command({ _id: EntityViewSortDirection.Asc, iconLeft: 'fa-regular fa-arrow-down-short-wide', tooltip: 'Ascending: A to Z, low to high, oldest to newest' }), new Command({ _id: EntityViewSortDirection.Desc, iconLeft: 'fa-regular fa-arrow-up-short-wide', tooltip: 'Descending: Z to A, high to low, newest to oldest' })];

    const display = view?.display ?? EntityViewDisplay.Table;
    const isGanttDisplay = display === EntityViewDisplay.Gantt;
    const isListDisplay = display === EntityViewDisplay.List;
    const showDisplayProperties = isListDisplay && listDisplayPropertiesEnabled(listConfig);
    const showTitleLines = isListDisplay && compact;
    const showEmptyGroupsMode = isListDisplay && listConfig?.showEmptyGroups === 'opt-in';
    // Nested show/indent is NOT hard-coded here — declare displaySettings.contributions on the
    // entity manifest (Task, File, SOR items, …). list.nested stays structural only.
    // Chip / row field order for display properties (Linear appearance order). Filled when chips build.
    let displayPropertyAppearanceOrder: string[] = [];

    // Declared ahead of the rows because the layout action reads the live selection back out of it.
    let form: SettingsPanelForm | null = null;

    const builder = new SettingsPanelBuilder('Display Settings');
    builder.footerRight(new Command({ _id: 'resetDisplayDefaults', label: 'Reset to defaults', action: () => this.resetRequested.emit() }));
    // Compact overlay: Reset is a flush text action (underline hover).

    // Layout, first, because it reframes every row under it: Group and Sorting mean different things
    // on a Kanban than on a Table. The choice is the USER's and personal to them — the view keeps
    // declaring the default everyone else lands on until someone with edit rights promotes this one.
    //
    // Two gates, and both are needed. `showDisplaySwitcher` is the mount saying this board's audience
    // has a real choice to make. A single offered type is no choice at all, whatever the mount asked
    // for, and `lockDisplay` is a context that pins the layout, where a switcher would write a
    // preference the board then ignores.
    const switcherTypes = resolveDisplayTypes(displayTypes, display);
    const showSwitcher = showDisplaySwitcher && !lockDisplay && switcherTypes.length > 1;
    // Read through the finished form rather than capturing a value: the action fires long after the
    // panel is built, on whatever segment the user has landed on by then.
    const selectedDisplay = (): EntityViewDisplay => (form?.get('display') as EntityViewDisplay) ?? display;

    if (showSwitcher) {
      builder.select('display', 'Layout', {
        commands: switcherTypes.map(type => displayTypeCommand(type)).filter((command): command is Command => !!command),
        selected: display,
      });

      // Promotion is a WRITE to the shared view, so it is an action rather than a second segmented
      // control: it is offered only to someone who may edit the view, and only while their personal
      // layout differs from the one the view declares — otherwise it promotes what is already there.
      if (canEditView) {
        builder.action('setDefaultDisplay', 'Set as default for everyone', {
          action: () => this.defaultDisplayRequested.emit(selectedDisplay()),
          hidden: values => ((values as { display?: EntityViewDisplay }).display ?? display) === (viewDisplay ?? display),
        });
      }

      builder.divider();
    }

    if (isGanttDisplay) {
      const gantt = view?.gantt;
      const fields = (config.uiFields ?? []).filter(isGanttColumnField).map(field => ({ id: field.path ?? field.name, label: field.displayName ?? field.name }));
      builder.formGroup('gantt', group => group
        .picker('columns', 'Columns', { mode: 'multi', reorderable: true, options: fields, selected: [...(gantt?.columns ?? [gantt?.startField, gantt?.finishField].filter(Boolean) as string[])] })
        .dropdown('taskPane', 'Task display', { options: [{ id: 'grid', label: 'Grid' }, { id: 'cards', label: 'Cards' }], selected: gantt?.taskPane ?? 'grid' })
        .dropdown('scale', 'Time scale', { options: ['hours', 'days', 'weeks', 'months', 'years'].map(id => ({ id, label: id[0].toUpperCase() + id.slice(1) })), selected: gantt?.scale ?? 'days' }));
    }

    // Show Group for table/list/attachments boards (stages). Options depend on recordScope.
    // Kanban is never here: its axes belong to the view definition, not runtime appearance.
    const isAttachmentsDisplay = display === EntityViewDisplay.Attachments;
    const groupDisplayAllowed = display === EntityViewDisplay.Table || isListDisplay || isAttachmentsDisplay;
    // A dropdown whose only option is 'None' is dead UI — omit Group entirely when nothing is
    // groupable (no groupable metadata fields, or metadata has not loaded yet).
    const showGroup = groupDisplayAllowed && groupableOptions.length > 1;
    const parentGroupPaths = resolveParentGroupPaths(config);

    if (showGroup) {
      // List boards mirror Linear "Grouping"; table/attachments keep the shorter "Group".
      builder.dropdown('groupBy', isListDisplay ? 'Grouping' : 'Group', {
        options: groupableOptions,
        selected: currentGroupBy,
      });
      if (display === EntityViewDisplay.Table) {
        // Then by is offered only over a PARENT group. A second key at row grain collapses the
        // server back to flat row paging (`resolveEmbeddedPaging` sets rowGroupBy only for a lone
        // row-level key), so the groups the table drew would straddle pages — a group-complete
        // promise the board cannot keep. Parent grain pages whole parent documents either way.
        builder.dropdown('subGroupBy', 'Then by', {
          options: groupableOptions.filter(option => option.id !== currentGroupBy),
          selected: normalizeGroupBy(table?.subGroupBy) ?? 'none',
          hidden: values => {
            const chosen = normalizeGroupBy((values as { groupBy?: string }).groupBy);
            if (chosen == null) return true;
            return parentGroupPaths != null && !parentGroupPaths.has(chosen);
          },
        });
      }
    }

    builder.compound(isListDisplay ? 'Ordering' : 'Sorting', (s) => s
      .dropdown('sortBy', '', { options: sortableOptions, selected: selectedSortId })
      .select('sortOrder', '', { selected: currentSort.direction, commands: sortCommands, hidden: values => manualSortField != null && (values as { sortBy?: string }).sortBy === manualSortField })
    );

    // Unpaged boards have no pager, so pageSize is a no-op.
    if (paged) {
      builder.dropdown('pageSize', 'Page Size', {
        selected: clampUnvirtualizedBoardPageSize(config.pageSize ?? 50, display),
        options: boardPageSizeOptions(display, 'display'),
      });
    }

    // Remaining list shell controls that still need field-aware builders (chips from metadata).
    // Nested show/indent are NOT here — declare displaySettings.contributions on the manifest.
    if (showDisplayProperties || showTitleLines || showEmptyGroupsMode) {
      builder.divider();
      // Section header: same size as row labels, flush with content (no extra indent).
      builder.group('List options');

      if (showTitleLines) {
        builder.dropdown('titleLines', 'Title lines', {
          selected: list?.titleLines ?? 1,
          options: [
            { id: 1, label: '1 line' },
            { id: 2, label: '2 lines' },
            { id: 3, label: '3 lines' },
          ],
        });
      }

      if (showEmptyGroupsMode) {
        // Always visible (disabled when flat) so toggling Group doesn't jank the panel height.
        builder.switch('showEmptyGroups', 'Show empty groups', {
          checked: list?.showEmptyGroups === true,
          disabled: (values) => normalizeGroupBy((values as { groupBy?: string }).groupBy ?? currentGroupBy) == null,
        });
      }

      if (showDisplayProperties) {
        // Linear: label + wrapping toggle chips. Order = row appearance order (allowlist, then
        // current selection, then rest) — not alphabetical (toFieldOptions sorts by label).
        const viewList = view?.list;
        const uiFields = config.uiFields ?? [];
        const allow = listDisplayPropertiesAllow(listConfig);
        const selectedRaw = constrainListDisplayPropertySelection(allow, (viewList?.fields ?? []).map(String));
        const propertyFields = resolveListDisplayPropertyFields(uiFields, {
          allow,
          exclude: [
            viewList?.identifier as string | undefined,
            viewList?.title as string | undefined,
            viewList?.status as string | undefined,
          ],
        });
        const byId = new Map(propertyFields.map(f => [String(f.path ?? f.name), f]));
        if (!allow?.length) {
          for (const id of selectedRaw) {
            if (byId.has(id)) continue;
            const field = uiFields.find(f => (f.path ?? f.name) === id);
            if (field) byId.set(id, field);
          }
        }
        const orderedFields = orderListDisplayPropertyFields(byId, allow, selectedRaw);
        displayPropertyAppearanceOrder = orderedFields.map(f => String(f.path ?? f.name));
        const selectedFields = sortIdsByAppearance(selectedRaw, displayPropertyAppearanceOrder);
        builder.chips('listFields', 'Display properties', {
          options: orderedFields.map(f => ({
            id: String(f.path ?? f.name),
            label: f.displayName ?? f.name ?? String(f.path ?? f.name),
          })),
          selected: selectedFields,
        });
      }
    }

    // Generic contributions last among body options (before preview): file-scope, task nested, …
    // Shell only paints control kinds — no entity-named branches.
    const activeContributions = resolveActiveDisplaySettingsContributions(displaySettings, {
      display,
      view: view ?? null,
      isEmbedded,
      compact,
    });
    appendDisplaySettingsContributions(builder, activeContributions, view);

    if (previewSupported) {
      // Per-view document preview toggle (absent on the view = on), rendered LAST as its own
      // divider-separated section. The caller passes previewSupported only when the board declares a
      // `preview` panel, so there is no display check here: that was a proxy for "Table + financial
      // document" from before the manifest said which boards have one, and it now contradicts it.
      // A quickview never reaches this — it is selection-driven and has nothing to toggle.
      builder.divider();
      builder.switch('showPreview', 'Show Preview', {
        checked: view?.showPreview !== false,
      });
    }

    form = builder.build();

    form.onUpdate((path, _value) => {
      const values = form!.value() as Record<string, any>;
      const groupBy = normalizeGroupBy(values.groupBy);
      const subGroupBy = groupBy && values.subGroupBy !== groupBy ? normalizeGroupBy(values.subGroupBy) : null;
      const nextDisplay = (showSwitcher ? values.display as EntityViewDisplay : undefined) ?? view?.display;
      const pageSize = paged
        ? clampUnvirtualizedBoardPageSize(Number(values.pageSize) || config.pageSize || 50, nextDisplay)
        : (config.pageSize ?? 50);

      // Keep list.fields in chip appearance order so the row matches Linear (not toggle-click order).
      const listFieldsRaw = (values.listFields ?? list?.fields ?? []) as string[];
      const listFieldsOrdered = showDisplayProperties
        ? sortIdsByAppearance(listFieldsRaw.map(String), displayPropertyAppearanceOrder)
        : listFieldsRaw;

      const titleLines = Math.min(3, Math.max(1, Number(values.titleLines) || list?.titleLines || 1)) as 1 | 2 | 3;
      const sortDirection = values.sortBy === manualSortField ? EntityViewSortDirection.Asc : values.sortOrder;
      const listPatch = showDisplayProperties || showTitleLines || showEmptyGroupsMode
        ? {
            list: {
              ...(view?.list as object),
              ...(showDisplayProperties ? { fields: listFieldsOrdered } : {}),
              ...(showTitleLines ? { titleLines } : {}),
              ...(showEmptyGroupsMode ? { showEmptyGroups: values.showEmptyGroups === true } : {}),
            },
          }
        : {};

      // Stamp contribution paths when the user touched a contribution control, or the view already
      // holds values for any contribution path (extensions.* or list.showNestedItems, etc.).
      // Avoids writing form defaults on page-size/group changes alone.
      const contribControlIds = new Set(
        activeContributions.flatMap(c => (c.controls ?? []).map(ctrl => ctrl.id)),
      );
      const includeContributions = contribControlIds.has(String(path ?? ''))
        || viewHasContributionPathValues(activeContributions, view);
      // Contributions preserve sibling values from their base; use this edit, not the old field selection.
      const contributionFragment = includeContributions
        ? contributionFormValuesToViewFragment(activeContributions, values, view?.list ? { ...view, list: { ...view.list, ...listPatch.list } } : view)
        : {};

      // Deep-merge `list` when both the shell list patch and contributions write it
      // (display properties + nested switches must not clobber each other).
      const listFromPatch = (listPatch as { list?: Record<string, unknown> }).list;
      const listFromContrib = (contributionFragment as unknown as { list?: Record<string, unknown> }).list;
      const mergedList = mergePlainObjects(listFromPatch, listFromContrib);

      // A layout change reshapes the request (projection, kanban grouping, paging), so the host has
      // to refetch rather than just re-render what it already holds.
      this.valueChange.emit({
        displayChanged: showSwitcher && nextDisplay !== display,
        view: {
          display: nextDisplay,
          sort: values.sortBy && values.sortBy !== 'none' ? { _id: values.sortBy, direction: sortDirection } : null,
          ...(paged ? { pageSize } : {}),
          // Absent Group control = no opinion. Omitting `table` keeps the consumer's owned groupBy
          // instead of resetting it to None on an unrelated change (page size, sort, …).
          ...(showGroup
            ? {
                table: {
                  ...(view?.table as object),
                  groupBy,
                  ...(display === EntityViewDisplay.Table ? { subGroupBy } : {}),
                },
              }
            : {}),
          ...(isGanttDisplay ? { gantt: ganttFormToConfig(view?.gantt, values.gantt, config.uiFields) } : {}),
          ...listPatch,
          ...contributionFragment,
          ...(mergedList ? { list: mergedList } : {}),
          // Only stamp showPreview when the entity actually supports the preview pane.
          ...(previewSupported ? { showPreview: values.showPreview !== false } : {}),
        } as Partial<EntityView>,
        pageSize,
      });
    });

    return form;
  }
}
